// Componente Input reutilizable para la aplicación Amarsi Control Panel
// Proporciona campos de entrada personalizados con iconos, validación y estados

import React, { forwardRef } from 'react';
import { FiMail, FiLock, FiUser, FiPhone, FiCalendar } from 'react-icons/fi';
import { cn } from '../../utils/cn';

/**
 * Componente Input
 * 
 * Campo de entrada reutilizable con soporte para:
 * - Iconos izquierdo y derecho
 * - Estados de validación
 * - Placeholder personalizado
 * - Tipos de entrada específicos
 */
const Input = forwardRef(({
  type = 'text',
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  required = false,
  icon,
  rightIcon,
  onRightIconClick,
  iconPosition = 'left',
  variant = 'default',
  size = 'medium',
  className = '',
  helperText,
  ...props
}, ref) => {
  // Maneja el cambio de valor pasando el evento completo para mantener compatibilidad
  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };
  // Mapeo de iconos predefinidos
  const iconMap = {
    email: FiMail,
    password: FiLock,
    user: FiUser,
    phone: FiPhone,
    calendar: FiCalendar,
  };

  // Obtiene el componente de icono
  const LeftIcon = iconMap[icon] || icon;
  const RightIcon = rightIcon;

  // Define las clases base según la variante
  const variantClasses = {
    default: 'border-zinc-300 focus:border-blue-500 focus:ring-blue-500 dark:border-zinc-600 dark:focus:border-blue-400 dark:focus:ring-blue-400',
    error: 'border-red-300 focus:border-red-500 focus:ring-red-500 dark:border-red-400 dark:focus:border-red-400 dark:focus:ring-red-400',
    success: 'border-green-300 focus:border-green-500 focus:ring-green-500 dark:border-green-400 dark:focus:border-green-400 dark:focus:ring-green-400',
  };

  // Define las clases de tamaño
  const sizeClasses = {
    small: 'py-1.5 px-3 text-sm',
    medium: 'py-2 px-3 text-base',
    large: 'py-3 px-4 text-lg',
  };

  // Determina la variante basada en el error
  const currentVariant = error ? 'error' : variant;

  // Clases para el input
  const inputClasses = cn(
    'w-full border rounded-md placeholder-zinc-400 dark:placeholder-zinc-500',
    'focus:outline-none focus:ring-2 transition-colors duration-200',
    'disabled:bg-neutral-50 disabled:text-zinc-500 disabled:cursor-not-allowed dark:bg-neutral-700 dark:text-white dark:disabled:bg-neutral-600 dark:disabled:text-zinc-400',
    variantClasses[currentVariant],
    sizeClasses[size],
    LeftIcon && 'pl-10',
    RightIcon && 'pr-10',
    className
  );

  // Clases para el contenedor del icono
  const iconContainerClasses = 'absolute inset-y-0 flex items-center pointer-events-none';
  const leftIconClasses = cn(iconContainerClasses, 'left-0 pl-3');
  const rightIconClasses = cn('absolute inset-y-0 flex items-center right-0 pr-3', onRightIconClick && 'pointer-events-auto');

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={props.id} className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1 text-left">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {/* Icono izquierdo */}
        {LeftIcon && (
          <div className={leftIconClasses}>
            <LeftIcon 
              className={cn(
                'w-5 h-5',
                error ? 'text-red-400 dark:text-red-300' : 'text-zinc-400 dark:text-zinc-500'
              )} 
            />
          </div>
        )}

        {/* Campo de entrada */}
        <input
          ref={ref}
          type={type}
          value={type === 'number' ? (value === 0 ? '0' : value || '') : value}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          className={inputClasses}
          placeholder={placeholder}
          {...props}
        />

        {/* Icono derecho */}
        {RightIcon && (
          <div 
            className={rightIconClasses}
            onClick={onRightIconClick}
          >
            <RightIcon 
              className={cn(
                'w-5 h-5 cursor-pointer hover:text-zinc-600 transition-colors dark:hover:text-zinc-300',
                error ? 'text-red-400 dark:text-red-300' : 'text-zinc-400 dark:text-zinc-500'
              )} 
            />
          </div>
        )}
      </div>

      {/* Mensaje de error */}
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {/* Texto de ayuda */}
      {helperText && !error && (
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
